import { CalendarPickerActionsHandle } from '@modules/common/components/DateRangePicker/CalendarPicker/CalendarPicker';
import { Preset } from '@modules/common/components/DateRangePicker/hooks/useDatePresets';
import { formatDateTimeRange } from '@modules/common/utils';
import { ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import Box from '@mui/material/Box';
import { DateRange } from '@mui/x-date-pickers-pro';
import { Dayjs } from 'dayjs';
import { isEqual } from 'lodash';
import { MouseEvent, useMemo, useRef } from 'react';
import { CalendarPicker } from './CalendarPicker';
import {
  getDateRange,
  getSelectedDateRangeOption,
  isMatchingPreset,
  trackDatePickerEvent,
} from './helpers';
import { useDatePresets } from './hooks';
import { DateTimeRange } from './types';

interface DateRangePickerProps {
  maxDate?: Dayjs;
  timeRange: DateTimeRange;
  isReadOnly?: boolean;
  shouldShowQuickLookupOptions?: boolean;
  quickOptionsFilters?: { skipYesterday: boolean };
  onChange: (timeRange: DateTimeRange, isQuickLookupOption?: boolean) => void;
  datePresets?: Preset[];
}

export function DateRangePicker({
  timeRange,
  isReadOnly = false,
  onChange,
  quickOptionsFilters,
  shouldShowQuickLookupOptions = true,
  datePresets: customDatePresets,
}: DateRangePickerProps) {
  const calenderPickerRef = useRef<CalendarPickerActionsHandle>(null);

  const defaultDatePresets = useDatePresets(
    shouldShowQuickLookupOptions,
    quickOptionsFilters,
  );

  const datePresets = customDatePresets ?? defaultDatePresets;

  const presetTimeRange = useMemo(() => {
    return datePresets.map(({ timeRange }) => timeRange);
  }, [datePresets]);

  const dateRange = getDateRange(timeRange);
  const selectedOption = getSelectedDateRangeOption(timeRange, presetTimeRange);

  const isCustomDateRange = 'custom' === selectedOption;
  const calendarDateRange = isCustomDateRange
    ? dateRange
    : ([null, null] as DateRange<Dayjs | null>);
  const calendarTimeRange = timeRange;

  const isPresetOptionSelected = (value: DateTimeRange) => {
    if (!timeRange) {
      return;
    }
    return isMatchingPreset(value, timeRange);
  };

  const openCalendarPicker = (event: MouseEvent) => {
    calenderPickerRef.current?.openCalendarPicker(event);
  };

  return (
    <Box>
      <ToggleButtonGroup
        className="date-range-picker"
        data-disabled={isReadOnly ? 'true' : 'false'}
        size="small"
        exclusive
        disabled={isReadOnly}
        color="primary"
        value={selectedOption}
      >
        {datePresets.map(
          ({
            displayName,
            title,
            timeRange: value,
            dateRangeString: stringValue,
            renderPreset,
          }) => {
            if (renderPreset) {
              return renderPreset();
            }

            return (
              <ToggleButton
                key={displayName}
                value={stringValue}
                selected={isPresetOptionSelected(value)}
                onClick={() => {
                  if (!isEqual(timeRange, value)) {
                    onChange(value, true);
                    trackDatePickerEvent({
                      eventName: 'Datepicker - Preset Option Selected',
                      presetOption: displayName,
                      dateRange: formatDateTimeRange(value),
                    });
                  }
                }}
              >
                <Tooltip title={`${title} (in UTC)`} arrow placement="top">
                  <Box component="span">{displayName}</Box>
                </Tooltip>
              </ToggleButton>
            );
          },
        )}
        <ToggleButton value="custom" key="custom" onClick={openCalendarPicker}>
          <CalendarPicker
            ref={calenderPickerRef}
            shouldDisableTriggerClick
            dateRange={calendarDateRange}
            timeRange={calendarTimeRange}
            onChange={(newTimeRange) => {
              if (!isEqual(timeRange, newTimeRange)) {
                onChange(newTimeRange);
              }
            }}
          />
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}
